import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { CheckCircle2, Download, Home, RotateCcw, Share2 } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { VoiceChangeCta } from "@/components/voice/VoiceChangeOption";
import { AutoVoiceChange } from "@/components/voice/AutoVoiceChange";
import { Card } from "@/components/ui/card";
import { track } from "@/lib/analytics";
import { formatCategorySummary } from "@/lib/transformationCategories";

export const Route = createFileRoute("/_authenticated/create/template/$id/result")({
  head: () => ({
    meta: [
      { title: "完成 | 変身テンプレ | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ResultPage,
});

type Row = {
  status: string | null;
  template_state: string | null;
  output_video_url: string | null;
  intermediate_image_url: string | null;
  error_message: string | null;
  category_id: string | null;
  category_params: Record<string, unknown> | null;
};

function ResultPage() {
  const { id } = useParams({ from: "/_authenticated/create/template/$id/result" });
  const [row, setRow] = useState<Row | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    let active = true;
    const fetchOnce = async () => {
      const { data } = await supabase
        .from("generations")
        .select(
          "status, template_state, output_video_url, intermediate_image_url, error_message, category_id, category_params",
        )
        .eq("id", id)
        .maybeSingle();
      if (!active || !data) return;
      setRow(data as Row);
      if (!firedRef.current && data.template_state === "completed" && data.output_video_url) {
        firedRef.current = true;
        track("template_step2_complete", { generation_id: id });
      }
    };
    void fetchOnce();
  }, [id]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: "KAGEM で作った変身動画", url });
      } catch {
        /* canceled */
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("リンクをコピーしました");
    }
  };

  const downloadBlob = async (
    url: string,
    filename: string,
    mime: string,
    description: string,
    ext: string,
  ) => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      const w = window as unknown as {
        showSaveFilePicker?: (opts: {
          suggestedName: string;
          types: { description: string; accept: Record<string, string[]> }[];
        }) => Promise<{
          createWritable: () => Promise<{
            write: (data: Blob) => Promise<void>;
            close: () => Promise<void>;
          }>;
        }>;
      };
      if (typeof w.showSaveFilePicker === "function") {
        try {
          const handle = await w.showSaveFilePicker({
            suggestedName: filename,
            types: [{ description, accept: { [mime]: [ext] } }],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          toast.success("ダウンロードしました");
          return;
        } catch (e) {
          if ((e as { name?: string })?.name === "AbortError") return;
        }
      }
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch {
      toast.error("ダウンロードに失敗しました");
    }
  };

  const handleDownload = () => {
    if (!row?.output_video_url) return;
    track("template_download", { generation_id: id, kind: "video" });
    void downloadBlob(
      row.output_video_url,
      `kagem-transform-${id}.mp4`,
      "video/mp4",
      "MP4 動画",
      ".mp4",
    );
  };


  return (
    <main className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <header className="mb-6 text-center">
          <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="h-3.5 w-3.5" />
            完成
          </div>
          <h1 className="text-2xl font-bold sm:text-3xl">変身動画ができました</h1>
        </header>

        <Card className="overflow-hidden">
          <div className="aspect-[9/16] w-full bg-black">
            {row?.output_video_url ? (
              <video
                src={row.output_video_url}
                className="h-full w-full"
                controls
                playsInline
                autoPlay
                loop
              />
            ) : row?.intermediate_image_url ? (
              <img
                src={row.intermediate_image_url}
                alt="合成画像"
                className="h-full w-full object-contain"
              />
            ) : (
              <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                読み込み中...
              </div>
            )}
          </div>
        </Card>

        {row?.category_id && (
          <div className="mt-4 rounded-lg border border-border bg-card/50 p-3">
            <div className="mb-1 text-xs text-muted-foreground">変身設定</div>
            <div className="text-sm font-medium">
              {formatCategorySummary(row.category_id, row.category_params)}
            </div>
          </div>
        )}

        <div className="mt-6 grid grid-cols-2 gap-3">
          <Button variant="outline" onClick={handleShare}>
            <Share2 className="mr-1 h-4 w-4" />
            共有
          </Button>
          <Button onClick={handleDownload} disabled={!row?.output_video_url}>
            <Download className="mr-1 h-4 w-4" />
            動画をDL
          </Button>
        </div>

        <div className="mt-3 space-y-3">
          <AutoVoiceChange videoUrl={row?.output_video_url ?? null} />
          <VoiceChangeCta className="w-full" />
        </div>


        <div className="mt-3 grid grid-cols-2 gap-3">
          <Button variant="ghost" asChild>
            <Link
              to="/create/template"
              search={{ from: id } as never}
            >
              <RotateCcw className="mr-1 h-4 w-4" />
              もう一度作る
            </Link>
          </Button>
          <Button variant="ghost" asChild>
            <Link to="/dashboard">
              <Home className="mr-1 h-4 w-4" />
              ダッシュボードへ
            </Link>
          </Button>
        </div>

      </div>
    </main>
  );
}
